import { useEffect, useState } from 'react';
import { RotateCcw } from 'lucide-react';
import { Modal } from '../../components/Modal';
import { Button, Field } from '../../components/ui';
import { Checkbox, Select, Textarea } from '../../components/form';
import { useToast } from '../../components/Toast';
import { useFormErrors } from '../../lib/useFormErrors';
import { useCreateSession } from '../../api/queries';
import type { Group } from '../../api/types';

/**
 * დამატებითი გაკვეთილი განრიგში — ჩვეულებრივი ან **აღდგენითი**.
 * აღდგენა ცალკე აღინიშნება, რომ განრიგში გამორჩეული იყოს.
 */
export function ExtraSessionModal({
  open,
  onClose,
  groups,
  defaultDate,
  defaultGroupId,
}: {
  open: boolean;
  onClose: () => void;
  groups: Group[];
  defaultDate: string;
  defaultGroupId?: string;
}) {
  const toast = useToast();
  const { fieldErrors, reset, handle } = useFormErrors();

  const [groupId, setGroupId] = useState('');
  const [date, setDate] = useState('');
  const [startTime, setStartTime] = useState('');
  const [topic, setTopic] = useState('');
  const [note, setNote] = useState('');
  const [isMakeup, setIsMakeup] = useState(true);

  const create = useCreateSession(groupId);
  const group = groups.find((g) => g.id === groupId);

  useEffect(() => {
    if (!open) return;
    reset();
    setGroupId(defaultGroupId || groups[0]?.id || '');
    setDate(defaultDate);
    setStartTime('');
    setTopic('');
    setNote('');
    setIsMakeup(true);
  }, [open, defaultDate, defaultGroupId, groups, reset]);

  // ჯგუფის არჩევისას დრო მისი განრიგიდან ისმება
  useEffect(() => {
    if (group && !startTime) setStartTime(group.startTime);
  }, [group, startTime]);

  async function submit() {
    reset();
    if (!groupId) {
      toast.error('აირჩიე ჯგუფი');
      return;
    }
    try {
      await create.mutateAsync({ date, startTime, topic, note, isMakeup });
      toast.success(isMakeup ? 'აღდგენითი გაკვეთილი დაემატა' : 'გაკვეთილი დაემატა');
      onClose();
    } catch (err) {
      handle(err);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="დამატებითი ლექცია"
      description="განრიგში ერთჯერადი გაკვეთილის ჩამატება — ნუმერაცია ეტაპის ბოლოს გაგრძელდება"
      footer={
        <>
          <Button variant="secondary" size="sm" onClick={onClose} disabled={create.isPending}>
            გაუქმება
          </Button>
          <Button size="sm" onClick={() => void submit()} loading={create.isPending}>
            დამატება
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Select
          label="ჯგუფი"
          value={groupId}
          onChange={(e) => {
            setGroupId(e.target.value);
            setStartTime('');
          }}
          error={fieldErrors.groupId}
          placeholder="აირჩიე ჯგუფი"
          options={groups.map((g) => ({ value: g.id, label: `${g.name} · ${g.course.name}` }))}
        />

        <div className="grid gap-4 sm:grid-cols-2">
          <Field
            label="თარიღი"
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            error={fieldErrors.date}
          />
          <Field
            label="დაწყების დრო"
            type="time"
            value={startTime}
            onChange={(e) => setStartTime(e.target.value)}
            error={fieldErrors.startTime}
            hint={group ? `ჯგუფის ჩვეული დრო: ${group.startTime}` : undefined}
          />
        </div>

        <Field
          label="თემა"
          value={topic}
          onChange={(e) => setTopic(e.target.value)}
          error={fieldErrors.topic}
          placeholder="არასავალდებულო — ლექტორი შეავსებს"
        />

        <div className="rounded-lg border border-line bg-canvas/50 p-3">
          <Checkbox
            label="აღდგენითი გაკვეთილი"
            checked={isMakeup}
            onChange={setIsMakeup}
            hint="განრიგში ცალკე აღინიშნება"
          />
          {isMakeup && (
            <p className="mt-2 flex items-center gap-1.5 text-[12px] text-warn">
              <RotateCcw className="size-3.5" />
              გაცდენილი გაკვეთილის ნაცვლად ტარდება
            </p>
          )}
        </div>

        <Textarea
          label="შენიშვნა"
          rows={2}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          error={fieldErrors.note}
          placeholder="მაგ. 12 სექტემბრის გაკვეთილის აღდგენა"
        />
      </div>
    </Modal>
  );
}
